import { database } from './firebase';
import { ref, push, set, onValue, update, remove, query, orderByChild } from 'firebase/database';

export class PedidosService {
  static async crearPedido(pedido) {
    try {
      // Guardar en Realtime Database
      const pedidosRef = ref(database, 'pedidos');
      const nuevoPedidoRef = push(pedidosRef);
      const pedidoConId = {
        ...pedido,
        id: nuevoPedidoRef.key,
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      
      await set(nuevoPedidoRef, pedidoConId);
      
      // También guardar en localStorage como respaldo
      this.guardarEnLocalStorage(pedidoConId);
      
      return pedidoConId;
    } catch (error) {
      console.error('Error al crear pedido en Realtime Database:', error);
      
      // Si Firebase falla, guardar solo en localStorage
      const id = 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const pedidoConId = {
        ...pedido,
        id: id,
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      
      this.guardarEnLocalStorage(pedidoConId);
      return pedidoConId;
    }
  }

  static async actualizarPedido(pedidoId, datos) {
    try {
      // Actualizar en Realtime Database
      const pedidoRef = ref(database, `pedidos/${pedidoId}`);
      const datosActualizados = {
        ...datos,
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      
      await update(pedidoRef, datosActualizados);
      
      // También actualizar en localStorage
      this.actualizarEnLocalStorage(pedidoId, datos);
      
      return datosActualizados;
    } catch (error) {
      console.error('Error al actualizar pedido en Realtime Database:', error);
      
      // Si Firebase falla, actualizar solo en localStorage
      this.actualizarEnLocalStorage(pedidoId, datos);
      return datos;
    }
  }

  static async eliminarPedido(pedidoId) {
    try {
      // Eliminar de Realtime Database
      const pedidoRef = ref(database, `pedidos/${pedidoId}`);
      await remove(pedidoRef);
      
      // También eliminar de localStorage
      this.eliminarDeLocalStorage(pedidoId);
    } catch (error) {
      console.error('Error al eliminar pedido de Realtime Database:', error);
      
      // Si Firebase falla, eliminar solo de localStorage
      this.eliminarDeLocalStorage(pedidoId);
    }
  }

  static suscribirsePedidos(callback) {
    try {
      // Primero intentar obtener datos de localStorage inmediatamente
      const pedidosLocal = this.obtenerDeLocalStorage();
      
      // Si hay datos en localStorage, mostrarlos inmediatamente
      if (pedidosLocal.length > 0) {
        callback(pedidosLocal);
      }
      
      // Conectar con Realtime Database
      const pedidosRef = ref(database, 'pedidos');
      return onValue(pedidosRef, (snapshot) => {
        const pedidosFirebase = [];
        
        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            pedidosFirebase.push(child.val());
          });
        }
        
        // También obtener de localStorage y combinar
        const pedidosLocalActualizados = this.obtenerDeLocalStorage();
        const pedidosCombinados = [...pedidosFirebase, ...pedidosLocalActualizados];
        
        // Eliminar duplicados
        const pedidosUnicos = pedidosCombinados.filter((pedido, index, self) => 
          index === self.findIndex(p => p.id === pedido.id)
        );
        
        callback(pedidosUnicos);
      }, (error) => {
        console.error('Error en suscripción de pedidos:', error);
        
        // Si la suscripción falla, usar solo localStorage
        const pedidosLocal = this.obtenerDeLocalStorage();
        callback(pedidosLocal);
      });
    } catch (error) {
      console.error('Error al suscribirse a pedidos:', error);
      
      // Si hay error, usar solo localStorage
      const pedidosLocal = this.obtenerDeLocalStorage();
      callback(pedidosLocal);
      
      // Devolver una función de limpieza vacía
      return () => {};
    }
  }

  static async obtenerPedidos() {
    try {
      const pedidosRef = ref(database, 'pedidos');
      
      return new Promise((resolve, reject) => {
        onValue(pedidosRef, (snapshot) => {
          const pedidosFirebase = [];
          
          if (snapshot.exists()) {
            snapshot.forEach((child) => {
              pedidosFirebase.push(child.val());
            });
          }
          
          // También obtener de localStorage y combinar
          const pedidosLocal = this.obtenerDeLocalStorage();
          const pedidosCombinados = [...pedidosFirebase, ...pedidosLocal];
          
          // Eliminar duplicados
          const pedidosUnicos = pedidosCombinados.filter((pedido, index, self) => 
            index === self.findIndex(p => p.id === pedido.id)
          );
          
          resolve(pedidosUnicos);
        }, reject);
      });
    } catch (error) {
      console.error('Error al obtener pedidos de Realtime Database:', error);
      
      // Si Firebase falla, devolver solo de localStorage
      return this.obtenerDeLocalStorage();
    }
  }

  // Métodos de localStorage
  static guardarEnLocalStorage(pedido) {
    try {
      const pedidosExistentes = this.obtenerDeLocalStorage();
      pedidosExistentes.push(pedido);
      localStorage.setItem('pedidos_backup', JSON.stringify(pedidosExistentes));
    } catch (error) {
      console.error('Error al guardar en localStorage:', error);
    }
  }

  static obtenerDeLocalStorage() {
    try {
      const pedidos = localStorage.getItem('pedidos_backup');
      return pedidos ? JSON.parse(pedidos) : [];
    } catch (error) {
      console.error('Error al obtener de localStorage:', error);
      return [];
    }
  }

  static actualizarEnLocalStorage(id, datos) {
    try {
      const pedidos = this.obtenerDeLocalStorage();
      const index = pedidos.findIndex(p => p.id === id);
      
      if (index !== -1) {
        pedidos[index] = {
          ...pedidos[index],
          ...datos,
          fechaActualizacion: new Date().toISOString(),
          timestamp: Date.now()
        };
        localStorage.setItem('pedidos_backup', JSON.stringify(pedidos));
      }
    } catch (error) {
      console.error('Error al actualizar en localStorage:', error);
    }
  }

  static eliminarDeLocalStorage(id) {
    try {
      const pedidos = this.obtenerDeLocalStorage();
      const pedidosFiltrados = pedidos.filter(p => p.id !== id);
      localStorage.setItem('pedidos_backup', JSON.stringify(pedidosFiltrados));
    } catch (error) {
      console.error('Error al eliminar de localStorage:', error);
    }
  }

  static async eliminarTodosLosRegistros() {
    try {
      // Eliminar de Realtime Database
      const pedidosRef = ref(database, 'pedidos');
      await remove(pedidosRef);
      
      // También eliminar de localStorage
      localStorage.removeItem('pedidos_backup');
      
      return true;
    } catch (error) {
      console.error('Error al eliminar todos los pedidos:', error);
      
      // Si Firebase falla, eliminar solo de localStorage
      localStorage.removeItem('pedidos_backup');
      
      throw error;
    }
  }
}
